import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from '@/config';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Award, BookOpen, Clock, CheckCircle2, Shield, Share2, Download,
  Star, Loader2, Copy, ExternalLink, GraduationCap, Target, Briefcase,
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

// ── Skill badge colors ──
const SKILL_COLORS = {
  'Digital Skills': 'bg-blue-100 text-blue-700 border-blue-200',
  'Communication': 'bg-purple-100 text-purple-700 border-purple-200',
  'Health & Safety': 'bg-red-100 text-red-700 border-red-200',
  'Food Preparation': 'bg-amber-100 text-amber-700 border-amber-200',
  'Conflict Resolution': 'bg-orange-100 text-orange-700 border-orange-200',
  'Mental Health Awareness': 'bg-violet-100 text-violet-700 border-violet-200',
  'Safeguarding': 'bg-rose-100 text-rose-700 border-rose-200',
  'Moving & Handling': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Fire Safety': 'bg-red-100 text-red-700 border-red-200',
  'Equality & Inclusion': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Person-Centred Care': 'bg-teal-100 text-teal-700 border-teal-200',
  'Record-Keeping': 'bg-slate-100 text-slate-700 border-slate-200',
};

// ── Shared Passport Content ──
const PassportContent = ({ data, isPublic, onShare, onDownloadPDF, shareUrl }) => {
  const { resident, skills, completed_courses, in_progress, certificates, stats } = data;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="passport-content">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-[#2a3e5d] to-[#1a2d4a] rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#02d1d4]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[#02d1d4] text-sm font-semibold tracking-wider uppercase">Independence Passport</p>
              <h1 className="text-3xl font-bold mt-2 text-white" data-testid="passport-name">{resident.name}</h1>
              <p className="text-white/60 text-sm mt-1">
                Member since {resident.joined ? new Date(resident.joined).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
            <Shield className="h-12 w-12 text-[#02d1d4]/30" />
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-8">
            {[
              { label: 'Completed', value: stats.total_completed, icon: CheckCircle2 },
              { label: 'In Progress', value: stats.total_in_progress || 0, icon: BookOpen },
              { label: 'Certificates', value: stats.total_certificates, icon: Award },
              { label: 'CPD Hours', value: stats.total_cpd_hours, icon: Clock },
              { label: 'Skills', value: stats.total_skills, icon: Target },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="text-center">
                  <Icon className="h-5 w-5 mx-auto mb-1 text-[#02d1d4]" />
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-white/50">{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Share + Download */}
      {!isPublic && (
        <div className="flex gap-3 flex-wrap">
          {shareUrl ? (
            <Button variant="outline" onClick={onShare} data-testid="share-passport-btn" className="gap-2">
              <Copy className="h-4 w-4" /> Copy Share Link
            </Button>
          ) : (
            <Button onClick={onShare} className="bg-[#20acf6] hover:bg-[#3bb8f7] gap-2" data-testid="enable-share-btn">
              <Share2 className="h-4 w-4" /> Enable Sharing
            </Button>
          )}
          <Button variant="outline" onClick={onDownloadPDF} data-testid="download-pdf-btn" className="gap-2">
            <Download className="h-4 w-4" /> Download as PDF
          </Button>
        </div>
      )}

      {/* Skills Achieved */}
      {skills.length > 0 && (
        <div data-testid="skills-section">
          <h2 className="text-lg font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-[#02d1d4]" /> Skills Achieved
          </h2>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, i) => (
              <Badge
                key={i}
                className={`px-3 py-1.5 text-sm font-medium border ${SKILL_COLORS[skill.name] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
                data-testid={`skill-${skill.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                {skill.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Certificates */}
      {certificates.length > 0 && (
        <div data-testid="certificates-section">
          <h2 className="text-lg font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-[#02d1d4]" /> Certificates Earned
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {certificates.map((cert, i) => (
              <Card key={i} className="border-slate-200 hover:border-[#02d1d4]/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#02d1d4]/10 rounded-lg flex items-center justify-center shrink-0">
                      <Award className="h-5 w-5 text-[#02d1d4]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-[#2a3e5d] line-clamp-1">{cert.course_title}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {cert.completion_date ? new Date(cert.completion_date).toLocaleDateString('en-GB') : ''} &middot; {cert.certificate_number}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        {cert.is_accredited && (
                          <Badge className="bg-purple-50 text-purple-600 border border-purple-200 text-[10px]">CPD Accredited</Badge>
                        )}
                        <Link to={`/verify/${cert.certificate_number}`} className="text-[10px] text-[#20acf6] hover:underline flex items-center gap-0.5">
                          <ExternalLink className="h-3 w-3" /> Verify
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Completed Courses */}
      {completed_courses.length > 0 && (
        <div data-testid="completed-courses-section">
          <h2 className="text-lg font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-[#02d1d4]" /> Completed Courses
          </h2>
          <div className="space-y-2">
            {completed_courses.map((course, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{course.course_title}</p>
                    <p className="text-xs text-slate-400">{course.category} &middot; {course.cpd_hours || 0} CPD hrs</p>
                  </div>
                </div>
                <span className="text-xs text-slate-400 shrink-0">
                  {course.completion_date ? new Date(course.completion_date).toLocaleDateString('en-GB') : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* In Progress */}
      {in_progress && in_progress.length > 0 && (
        <div data-testid="in-progress-section">
          <h2 className="text-lg font-bold text-[#2a3e5d] mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#20acf6]" /> In Progress
          </h2>
          <div className="space-y-3">
            {in_progress.map((course, i) => (
              <div key={i} className="p-3 border border-slate-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-800">{course.course_title}</p>
                  <span className="text-xs font-semibold text-[#20acf6]">{course.progress_pct}%</span>
                </div>
                <Progress value={course.progress_pct} className="h-2" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verified Badge */}
      <div className="text-center py-6 border-t border-slate-100">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
          <Shield className="h-4 w-4" /> Verified by Common Impact E-Learning
        </div>
      </div>
    </div>
  );
};

// ── My Passport (authenticated resident) ──
const MyPassport = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/passport/my');
        setData(res.data);
        if (res.data.resident?.passport_id) {
          setShareUrl(`${window.location.origin}/passport/${res.data.resident.passport_id}`);
        }
      } catch (e) {
        if (e.response?.status === 403) {
          toast.error('Passport is only available for resident accounts');
        } else {
          toast.error('Failed to load passport');
        }
      } finally { setLoading(false); }
    };
    fetch();
  }, [api]);

  const handleShare = async () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard');
    } else {
      try {
        const res = await api.post('/passport/enable');
        const pid = res.data.passport_id;
        const url = `${window.location.origin}/passport/${pid}`;
        setShareUrl(url);
        navigator.clipboard.writeText(url);
        toast.success('Sharing enabled. Link copied!');
      } catch { toast.error('Failed to enable sharing'); }
    }
  };

  const handleDownloadPDF = () => {
    if (!data) return;
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then(({ default: autoTable }) => {
        const doc = new jsPDF();
        const r = data.resident;

        doc.setFontSize(20);
        doc.setTextColor(42, 62, 93);
        doc.text('Independence Passport', 14, 22);

        doc.setFontSize(16);
        doc.text(r.name, 14, 34);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Member since: ${r.joined ? new Date(r.joined).toLocaleDateString('en-GB') : 'N/A'}`, 14, 42);
        doc.text(`Completed: ${data.stats.total_completed} | Certificates: ${data.stats.total_certificates} | CPD Hours: ${data.stats.total_cpd_hours}`, 14, 48);

        if (data.skills.length > 0) {
          doc.setFontSize(13);
          doc.setTextColor(42, 62, 93);
          doc.text('Skills Achieved', 14, 60);
          doc.setFontSize(10);
          doc.setTextColor(80);
          data.skills.forEach((s, i) => {
            doc.text(`• ${s.name}`, 18, 68 + i * 6);
          });
        }

        const startY = 68 + data.skills.length * 6 + 10;

        if (data.completed_courses.length > 0) {
          autoTable(doc, {
            startY,
            head: [['Course', 'Category', 'CPD Hours', 'Completed', 'Certificate']],
            body: data.completed_courses.map(c => [
              c.course_title, c.category, c.cpd_hours || 0,
              c.completion_date ? new Date(c.completion_date).toLocaleDateString('en-GB') : '-',
              c.certificate_number || '-',
            ]),
            headStyles: { fillColor: [42, 62, 93] },
            styles: { fontSize: 8 },
          });
        }

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Verified by Common Impact E-Learning', 14, doc.internal.pageSize.height - 10);
        if (shareUrl) {
          doc.text(`Share link: ${shareUrl}`, 14, doc.internal.pageSize.height - 15);
        }

        doc.save(`${r.name.replace(/\s+/g, '_')}_Passport.pdf`);
        toast.success('PDF downloaded');
      });
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <Shield className="h-14 w-14 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-[#2a3e5d]">Passport Unavailable</h2>
          <p className="text-sm text-slate-500 mt-1">Complete a course to start building your Independence Passport.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6">
        <PassportContent data={data} isPublic={false} onShare={handleShare} onDownloadPDF={handleDownloadPDF} shareUrl={shareUrl} />
      </div>
    </DashboardLayout>
  );
};

// ── Public Shared Passport ──
const SharedPassport = () => {
  const { passportId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/passport/share/${passportId}`)
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.data?.detail || 'Passport not found'))
      .finally(() => setLoading(false));
  }, [passportId]);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Header />
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
        ) : error ? (
          <div className="text-center py-20">
            <Shield className="h-14 w-14 text-slate-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-[#2a3e5d]">{error}</h2>
          </div>
        ) : data ? (
          <PassportContent data={data} isPublic={true} />
        ) : null}
      </div>
      <Footer />
    </div>
  );
};

export { MyPassport, SharedPassport };
export default MyPassport;
